import { useState, useEffect, useRef } from 'react';
import { readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { BG_CONTENT, TEXT_MUTED, BORDER_DEFAULT, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BG_MUTED, STATUS_NEUTRAL, ERROR_TEXT, SURFACE_WHITE, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    placeholder: 'Buscar persona…',
    addNew:      (name) => `Añadir "${name}" como nueva persona`,
  },
  en: {
    placeholder: 'Search person…',
    addNew:      (name) => `Add "${name}" as new person`,
  },
};

const WRITE_ERROR = {
  es: 'No se pudo guardar la persona. Comprueba el acceso a la carpeta del SGSI.',
  en: 'Could not save the person. Check access to the ISMS folder.',
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function nextPersonId(arr) {
  const maxSeq = arr.reduce((max, p) => {
    const m = /^person-(\d+)$/.exec(p.id || '');
    return m ? Math.max(max, parseInt(m[1], 10)) : max;
  }, 0);
  return 'person-' + String(maxSeq + 1).padStart(3, '0');
}

// If value matches a person.id, return that person's name. Otherwise return value as-is.
export function resolvePerson(value, people) {
  if (!value || !Array.isArray(people)) return value;
  const found = people.find(p => p.id === value);
  return found ? found.name : value;
}

// ── PEOPLE SELECTOR ───────────────────────────────────────────────────────────
// Autocomplete input backed by people.json.
// Props:
//   orgId       — org folder used to read / write people.json
//   lang        — 'es' | 'en'
//   value       — string (single) | string[] (multiple)
//   onChange    — (value) => void
//   multiple    — bool, default false
//   placeholder — string (optional override)
//   disabled    — bool, default false

export default function PeopleSelector({
  orgId,
  lang,
  value,
  onChange,
  multiple = false,
  placeholder,
  allowFreeText = false,
  disabled = false,
}) {
  const t = T[lang] || T.en;

  const [people,     setPeople]     = useState([]);
  const [localInput, setLocalInput] = useState(
    () => !multiple && typeof value === 'string' ? value : ''
  );
  const [open,        setOpen]        = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [writeError,  setWriteError]  = useState(null);

  const containerRef = useRef(null);
  // Keep a ref to the latest value so the outside-click handler can reset
  // localInput without needing value in its dependency array.
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);
  const localInputRef = useRef(localInput);
  useEffect(() => { localInputRef.current = localInput; }, [localInput]);
  const allowFreeTextRef = useRef(allowFreeText);
  useEffect(() => { allowFreeTextRef.current = allowFreeText; }, [allowFreeText]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  // Keep a ref to latest people so the outside-click handler can resolve names.
  const peopleRef = useRef(people);
  useEffect(() => { peopleRef.current = people; }, [people]);

  // ── Load people.json ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    readOrgFile(orgId, 'people.json')
      .then((data) => {
        if (!cancelled) setPeople(Array.isArray(data.people) ? data.people : []);
      })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Sync input to value prop (single mode only) ───────────────────────────
  useEffect(() => {
    if (!multiple) setLocalInput(resolvePerson(typeof value === 'string' ? value : '', people));
  }, [value, multiple, people]);

  // ── Outside click: close dropdown, reset input in single mode ────────────
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!multiple) {
          if (allowFreeTextRef.current) {
            const typed = localInputRef.current.trim();
            if (typed !== valueRef.current) onChangeRef.current(typed);
            setLocalInput(typed);
          } else {
            setLocalInput(resolvePerson(typeof valueRef.current === 'string' ? valueRef.current : '', peopleRef.current));
          }
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [multiple]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const selectedArr  = multiple ? (Array.isArray(value) ? value : []) : [];
  const activePeople = people.filter((p) => p.active === true);

  // Filter: exclude already-selected by ID (multiple), match typed text
  const filtered = activePeople
    .filter((p) => !multiple || !selectedArr.includes(p.id))
    .filter((p) => p.name.toLowerCase().includes(localInput.toLowerCase()));

  const trimmed    = localInput.trim();
  const showAddNew = trimmed !== ''
    && !activePeople.some((p) => p.name.toLowerCase() === trimmed.toLowerCase());

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelect = (id) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.includes(id)) onChange([...arr, id]);
      setLocalInput('');
      // keep dropdown open for further selections
    } else {
      onChange(id);
      setLocalInput(resolvePerson(id, people));
      setOpen(false);
    }
  };

  const handleAddNew = () => {
    if (disabled) return;
    const name = localInput.trim();
    if (!name) return;
    const newPerson = {
      id:         nextPersonId(people),
      name,
      email:      null,
      role:       null,
      department: null,
      country:    null,
      active:     true,
    };
    const updatedPeople = [...people, newPerson];
    setPeople(updatedPeople);
    writeOrgFile(orgId, 'people.json', {
      orgId,
      people:    updatedPeople,
      updatedAt: new Date().toISOString(),
    }).catch(() => {
      setWriteError(WRITE_ERROR[lang] || WRITE_ERROR.en);
    });
    handleSelect(newPerson.id);
  };

  const handleRemoveChip = (id) => {
    const arr = Array.isArray(value) ? value : [];
    onChange(arr.filter((n) => n !== id));
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const inputSt = {
    ...FONT_LABEL,
    width: '100%', boxSizing: 'border-box',
    padding: '0.45rem 0.65rem',
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 6,
    background: disabled ? BG_MUTED : BG_SURFACE,
    color: TEXT_DEFAULT, outline: 'none',
    cursor: disabled ? 'not-allowed' : undefined,
  };

  const dropdownSt = {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 200, maxHeight: 200, overflowY: 'auto', marginTop: 2,
  };

  const dropItemBase = {
    ...FONT_LABEL,
    display: 'block', width: '100%', textAlign: 'left',
    border: 'none', cursor: 'pointer',
    padding: '0.4rem 0.65rem',
    color: TEXT_DEFAULT,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ position: 'relative' }}>

      {/* Chips — multiple mode only */}
      {multiple && selectedArr.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.3rem' }}>
          {selectedArr.map((id) => (
            <span key={id} style={{
              ...FONT_TINY,
              display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
              background: '#e0e7ef', color: TEXT_DEFAULT, borderRadius: 4,
              padding: '0.12rem 0.35rem 0.12rem 0.45rem',
            }}>
              {resolvePerson(id, people)}
              <button
                type="button"
                onClick={() => handleRemoveChip(id)}
                disabled={disabled}
                style={{
                  background: 'none', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
                  color: TEXT_MUTED, padding: 0, fontSize: '0.85rem',
                  lineHeight: 1, display: 'flex', alignItems: 'center',
                  pointerEvents: disabled ? 'none' : undefined,
                }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {/* Text input */}
      <input
        type="text"
        value={localInput}
        placeholder={placeholder || t.placeholder}
        onChange={(e) => { if (disabled) return; setLocalInput(e.target.value); setOpen(true); setWriteError(null); }}
        onFocus={() => { if (disabled) return; setOpen(true); }}
        disabled={disabled}
        readOnly={disabled}
        style={inputSt}
      />
      {writeError && (
        <span style={{
          ...FONT_SMALL,
          display: 'block', marginTop: '0.25rem',
          color: ERROR_TEXT,
        }}>{writeError}</span>
      )}

      {/* Dropdown */}
      {open && (filtered.length > 0 || showAddNew) && (
        <div style={dropdownSt}>
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(p.id); }}
              onMouseEnter={() => setHoveredItem(p.id)}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                ...dropItemBase,
                background: hoveredItem === p.id ? BG_CONTENT : 'transparent',
              }}
            >
              {p.name}
            </button>
          ))}
          {showAddNew && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleAddNew(); }}
              onMouseEnter={() => setHoveredItem('__add__')}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                ...dropItemBase,
                color: BRAND_PRIMARY, fontSize: '0.75rem',
                borderTop: filtered.length > 0 ? `1px solid ${STATUS_NEUTRAL.bg}` : 'none',
                background: hoveredItem === '__add__' ? '#f0fdf4' : 'transparent',
              }}
            >
              {t.addNew(trimmed)}
            </button>
          )}
        </div>
      )}

    </div>
  );
}
